import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { deleteDoc, doc } from 'firebase/firestore';
import Swal from 'sweetalert2';
import { DB } from '~/services/firebase';

import Dashboard from '~/layouts/Dashboard';

import { useToast } from '~/hooks/toast';

import { useClients } from '~/hooks/clients';
import getCurrencyFormatted from '~/utils/getCurrencyFormatted';
import { useAuth } from '~/hooks/auth';
import { clearFormatPhoneNumber } from '~/utils/getPhoneFormatted';
import api from '~/services/heroku';
import { Pagination } from '~/components/Pagination';

const { REACT_APP_FIREBASE_PROJECT } = process.env;

let interval: any;

const Clients: React.FC = () => {
  const { addToast } = useToast();
  const { clients, loadClients, isInitialized, removeClient } = useClients();
  const { company } = useAuth();

  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('Todos');
  const [showFilter, setShowFilter] = useState(false);

  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!isInitialized) {
      loadClients();
    }
  }, [loadClients, isInitialized]);

  const handleChangeSearch = useCallback((value: string) => {
    if (interval) clearInterval(interval);

    interval = setInterval(() => {
      setSearch(value);
    }, 1000);
  }, []);

  const filtered = useMemo(() => {
    let count = 0;

    const items =
      search.length === 0 ? clients.slice((page - 1) * 20, page * 20) : clients;

    const filteredResult = items.filter(client => {
      let result = true;
      const whatsapp = clearFormatPhoneNumber(client.whatsapp);

      if (status === 'Ativo') {
        result =
          (String(client.name)
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase()) ||
            whatsapp.includes(search)) &&
          client.status === true;
      } else if (status === 'Inativo') {
        result =
          (String(client.name)
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase()) ||
            whatsapp.includes(search)) &&
          client.status === false;
      } else {
        result =
          String(client.name)
            .toLocaleLowerCase()
            .includes(search.toLocaleLowerCase()) || whatsapp.includes(search);
      }

      if (result && count < 20) {
        count += 1;
        return result;
      }

      return false;
    });
    return filteredResult;
  }, [search, clients, status, page]);

  const handleConfirmRemove = useCallback(
    async (id: string) => {
      const { isConfirmed } = await Swal.fire({
        title: 'Atenção!',
        text: 'Confirme a exclusão do cliente?',
        icon: 'warning',
        confirmButtonText: 'Sim',
        showCancelButton: true,
        cancelButtonText: 'Não',
      });

      if (isConfirmed) {
        await removeClient(id);

        addToast({
          type: 'success',
          description: 'Cliente foi removido com sucesso.',
        });
      }
    },
    [addToast, removeClient],
  );

  return (
    <Dashboard containerMessage="col-xl-8 offset-xl-2">
      <div className="col-xl-8 offset-xl-2">
        <div className="nk-block-head nk-block-head-sm">
          <div className="nk-block-between">
            <div className="nk-block-head-content">
              <h3 className="nk-block-title page-title">
                Clientes
                <span className="ml-1">{`(${clients.length})`}</span>
              </h3>
            </div>
            <div className="nk-block-head-content">
              <div className="toggle-wrap nk-block-tools-toggle">
                <button
                  type="button"
                  className={`btn btn-icon btn-trigger toggle-expand mr-n1 ${
                    showFilter && 'active'
                  }`}
                  data-target="pageMenu"
                  onClick={() => setShowFilter(current => !current)}
                >
                  <em className="icon ni ni-more-v" />
                </button>
                <div
                  className={`toggle-expand-content ${
                    showFilter && 'expanded'
                  }`}
                  data-content="pageMenu"
                >
                  <ul className="nk-block-tools g-3">
                    <li>
                      <div className="form-control-wrap">
                        <div className="form-icon form-icon-right">
                          <em className="icon ni ni-search" />
                        </div>
                        <input
                          type="text"
                          className="form-control"
                          id="default-04"
                          placeholder="Buscar por cliente"
                          onChange={event => {
                            handleChangeSearch(event.target.value);
                          }}
                        />
                      </div>
                    </li>
                    <li>
                      <div className="drodown">
                        <Link
                          to="#status"
                          className="dropdown-toggle dropdown-indicator btn btn-outline-light btn-white"
                          data-toggle="dropdown"
                        >
                          {status}
                        </Link>
                        <div className="dropdown-menu dropdown-menu-right">
                          <ul className="link-list-opt no-bdr">
                            <li>
                              <Link
                                to="#status"
                                onClick={() => setStatus('Todos')}
                              >
                                <span>Todos</span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#active"
                                onClick={() => setStatus('Ativo')}
                              >
                                <span>Ativo</span>
                              </Link>
                            </li>
                            <li>
                              <Link
                                to="#inactive"
                                onClick={() => setStatus('Inativo')}
                              >
                                <span>Inativo</span>
                              </Link>
                            </li>
                          </ul>
                        </div>
                      </div>
                    </li>
                    <li className="nk-block-tools-opt">
                      <Link
                        to="/clients/create"
                        data-target="addProduct"
                        className="toggle btn btn-icon btn-primary d-md-none"
                      >
                        <em className="icon ni ni-plus" />
                      </Link>
                      <Link
                        to="/clients/create"
                        data-target="addProduct"
                        className="toggle btn btn-primary d-none d-md-inline-flex"
                      >
                        <em className="icon ni ni-plus" />
                        <span>Cadastrar</span>
                      </Link>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="nk-block">
          <div className="card card-bordered">
            <div className="card-inner-group">
              <div className="card-inner p-0">
                <div className="nk-tb-list">
                  <div className="nk-tb-item nk-tb-head">
                    <div className="nk-tb-col">
                      <span>Nome</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Whatsapp</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Pedidos</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Fidelidade</span>
                    </div>
                    <div className="nk-tb-col">
                      <span>Status</span>
                    </div>
                    {company.sell_on_credit === '1' && (
                      <div className="nk-tb-col">
                        <span>Fiado</span>
                      </div>
                    )}

                    <div className="nk-tb-col nk-tb-col-tools">
                      <ul className="nk-tb-actions gx-1 my-n1">
                        <li className="mr-n1" />
                      </ul>
                    </div>
                  </div>

                  {filtered.map(item => (
                    <div className="nk-tb-item" key={item.uid}>
                      <div className="nk-tb-col">
                        <span className="title">{item.name}</span>
                      </div>
                      <div className="nk-tb-col">
                        <span className="title">{item.whatsapp}</span>
                      </div>
                      <div className="nk-tb-col">
                        <span className="title">
                          {item.order_count || 0} pedidos
                        </span>
                      </div>
                      <div className="nk-tb-col">
                        <span className="title">
                          {`${
                            item.fidelit_order_quantity || 0
                          } pedidos | ${getCurrencyFormatted(
                            item.fidelit_balance || 0,
                          )}`}
                        </span>
                      </div>
                      <div className="nk-tb-col">
                        <div className="asterisk tb-asterisk">
                          <span
                            className={`text-${
                              item.status ? 'success' : 'danger'
                            } fs-12px`}
                          >
                            {item.status && <span>Ativo</span>}
                            {!item.status && <span>Inativo</span>}
                          </span>
                        </div>
                      </div>

                      {company.sell_on_credit === '1' && (
                        <div className="nk-tb-col">
                          <Link
                            to={`/buyoncredit/${item.uid}`}
                            className="title"
                            style={{ cursor: 'pointer' }}
                          >
                            {item.buy_on_credit &&
                              getCurrencyFormatted(item.buy_on_credit)}
                          </Link>
                        </div>
                      )}
                      <div className="nk-tb-col nk-tb-col-tools">
                        <ul className="nk-tb-actions gx-1 my-n1">
                          <li className="mr-n1">
                            <div className="dropdown">
                              <Link
                                to="#options"
                                className="dropdown-toggle btn btn-icon btn-trigger"
                                data-toggle="dropdown"
                              >
                                <em className="icon ni ni-more-h" />
                              </Link>
                              <div className="dropdown-menu dropdown-menu-right">
                                <ul className="link-list-opt no-bdr">
                                  <li>
                                    <a
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      href={`https://api.whatsapp.com/send?phone=55${clearFormatPhoneNumber(
                                        item.whatsapp,
                                      )}&text=`}
                                    >
                                      <em className="icon ni ni-whatsapp" />
                                      <span>Whatsapp</span>
                                    </a>
                                  </li>
                                  <li>
                                    <Link
                                      to={{
                                        pathname: `/clients/orders/${item.uid}`,
                                        state: item,
                                      }}
                                    >
                                      <em className="icon ni ni-invest" />
                                      <span>Histórico de pedidos</span>
                                    </Link>
                                  </li>
                                  {/* <li>
                                    <Link
                                      to={`/clients/addresses/${item.uid}/create`}
                                    >
                                      <em className="icon ni ni-plus" />
                                      <span>Endereço</span>
                                    </Link>
                                  </li> */}
                                  <li>
                                    <Link to={`/clients/edit/${item.uid}`}>
                                      <em className="icon ni ni-edit" />
                                      <span>Editar</span>
                                    </Link>
                                  </li>

                                  <li>
                                    <Link
                                      to="#remove"
                                      onClick={() => {
                                        handleConfirmRemove(item.uid);
                                      }}
                                    >
                                      <em className="icon ni ni-trash" />
                                      <span>Excluir</span>
                                    </Link>
                                  </li>
                                </ul>
                              </div>
                            </div>
                          </li>
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="d-flex justify-content-end mt-2">
          <Pagination
            next={() => setPage(page + 1)}
            previous={() => {
              if (page > 1) {
                setPage(page - 1);
              }
            }}
          />
        </div>
      </div>
    </Dashboard>
  );
};

export default Clients;
